/**
 * User Configuration Service
 * 
 * Manages per-user, per-playlist, per-song configuration stored in localStorage.
 * Structure: { [userId]: { [playlistId]: { [songId]: { columnView: <VALUE>, ... } } } }
 */

import userStore from '../store/user.store.js';

const CONFIG_STORAGE_KEY = 'user-song-config';

/**
 * Get user ID from userStore
 * @returns {string|null} User ID or null if not available
 */
const getUserId = () => {
    try {
        const userInfo = userStore.state.userInfo;
        if (userInfo) {
            return userInfo.id || userInfo._id || null;
        }
    } catch (error) {
        console.error('Error getting user ID:', error);
    }
    return null;
};

/**
 * Get all user configurations from localStorage
 * @returns {Object} All configurations indexed by userId
 */
const getAllConfigs = () => {
    try {
        const configStr = localStorage.getItem(CONFIG_STORAGE_KEY);
        if (configStr) {
            return JSON.parse(configStr);
        }
    } catch (error) {
        console.error('Error reading user config from localStorage:', error);
    }
    return {};
};

/**
 * Save all user configurations to localStorage
 * @param {Object} configs - All configurations indexed by userId
 */
const saveAllConfigs = (configs) => {
    try {
        localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(configs));
    } catch (error) {
        console.error('Error saving user config to localStorage:', error);
    }
};

/**
 * Get configuration for a specific song
 * @param {string} userId - User ID
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @returns {Object|null} Configuration object or null if not found
 */
const getSongConfig = (userId, playlistId, songId) => {
    if (!userId || !playlistId || !songId) {
        return null;
    }

    const configs = getAllConfigs();
    return configs[userId]?.[playlistId]?.[songId] || null;
};

/**
 * Set configuration for a specific song
 * @param {string} userId - User ID
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @param {Object} config - Configuration object (e.g., { columnView: 'column-1' })
 */
const setSongConfig = (userId, playlistId, songId, config) => {
    if (!userId || !playlistId || !songId || !config) {
        console.warn('setSongConfig: Missing required parameters');
        return;
    }

    const configs = getAllConfigs();
    
    // Initialize nested structure if needed
    if (!configs[userId]) {
        configs[userId] = {};
    }
    if (!configs[userId][playlistId]) {
        configs[userId][playlistId] = {};
    }
    
    // Merge with existing config to preserve other properties
    const existingConfig = configs[userId][playlistId][songId] || {};
    configs[userId][playlistId][songId] = {
        ...existingConfig,
        ...config
    };
    
    saveAllConfigs(configs);
};

/**
 * Get a specific configuration property for a song
 * @param {string} userId - User ID
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @param {string} property - Property name (e.g., 'columnView')
 * @param {*} defaultValue - Default value if property not found
 * @returns {*} Property value or default value
 */
const getSongConfigProperty = (userId, playlistId, songId, property, defaultValue = null) => {
    const config = getSongConfig(userId, playlistId, songId);
    if (config && Object.prototype.hasOwnProperty.call(config, property)) {
        return config[property];
    }
    return defaultValue;
};

/**
 * Set a specific configuration property for a song
 * @param {string} userId - User ID
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @param {string} property - Property name (e.g., 'columnView')
 * @param {*} value - Value to set
 */
const setSongConfigProperty = (userId, playlistId, songId, property, value) => {
    const existingConfig = getSongConfig(userId, playlistId, songId) || {};
    setSongConfig(userId, playlistId, songId, {
        ...existingConfig,
        [property]: value
    });
};

/**
 * Get configuration for current user and song (convenience method)
 * Uses userStore to get current user ID
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @returns {Object|null} Configuration object or null if not found
 */
const getCurrentUserSongConfig = (playlistId, songId) => {
    const userId = getUserId();
    if (!userId) {
        return null;
    }
    return getSongConfig(userId, playlistId, songId);
};

/**
 * Set configuration for current user and song (convenience method)
 * Uses userStore to get current user ID
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @param {Object} config - Configuration object
 */
const setCurrentUserSongConfig = (playlistId, songId, config) => {
    const userId = getUserId();
    if (!userId) {
        console.warn('setCurrentUserSongConfig: User not logged in');
        return;
    }
    setSongConfig(userId, playlistId, songId, config);
};

/**
 * Get a specific configuration property for current user and song (convenience method)
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @param {string} property - Property name (e.g., 'columnView')
 * @param {*} defaultValue - Default value if property not found
 * @returns {*} Property value or default value
 */
const getCurrentUserSongConfigProperty = (playlistId, songId, property, defaultValue = null) => {
    const userId = getUserId();
    if (!userId) {
        return defaultValue;
    }
    return getSongConfigProperty(userId, playlistId, songId, property, defaultValue);
};

/**
 * Set a specific configuration property for current user and song (convenience method)
 * @param {string} playlistId - Playlist ID
 * @param {string} songId - Song ID
 * @param {string} property - Property name (e.g., 'columnView')
 * @param {*} value - Value to set
 */
const setCurrentUserSongConfigProperty = (playlistId, songId, property, value) => {
    const userId = getUserId();
    if (!userId) {
        console.warn('setCurrentUserSongConfigProperty: User not logged in');
        return;
    }
    setSongConfigProperty(userId, playlistId, songId, property, value);
};

export default {
    getSongConfig,
    setSongConfig,
    getSongConfigProperty,
    setSongConfigProperty,
    getCurrentUserSongConfig,
    setCurrentUserSongConfig,
    getCurrentUserSongConfigProperty,
    setCurrentUserSongConfigProperty,
    getUserId,
    getAllConfigs
};

